import { css, cva } from '@styled-system/css';
import { Crown, Languages, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@/app/providers/ThemeProvider';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { SettingsModal } from '@/features/settings/components/SettingsModal';
import { Avatar } from '@/shared/components/ui/Avatar';
import { DiscordIcon } from '@/shared/components/ui/DiscordIcon';
import { Logo } from '@/shared/components/ui/Logo';
import { Popover } from '@/shared/components/ui/Popover';
import { useUserLanguage } from '@/shared/hooks/useUserLanguage';

export const MOBILE_TOP_BAR_HEIGHT = 52;

const root = css({
  display: { base: 'flex', md: 'none' },
  // En flux normal (pas fixed) : le top bar prend sa propre hauteur dans le
  // layout, le contenu commence en dessous sans être coupé.
  flexShrink: 0,
  width: '100%',
  height: '52px',
  paddingX: '4',
  alignItems: 'center',
  gap: '3',
  bg: 'surface.default',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
});

const brand = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3',
  marginRight: 'auto',
  minWidth: 0,
});

const brandSquare = css({
  width: '22px',
  height: '22px',
  borderRadius: 'sm',
  bg: 'white',
  color: '#0A0A0A',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const brandText = css({
  fontSize: 'bodySm',
  fontWeight: 'bold',
  letterSpacing: '-0.3px',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const avatarTrigger = css({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  borderRadius: 'full',
  padding: '0.5',
  transition: 'background 120ms ease',
  _hover: { bg: 'surface.subtle' },
});

const menuList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  minWidth: '220px',
});

const menuItem = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    paddingY: '4',
    paddingX: '5',
    fontSize: 'bodySm',
    color: 'text.primary',
    borderRadius: 'sm',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    _hover: { bg: 'surface.sunken' },
  },
  variants: {
    danger: {
      true: { color: 'error.default', _hover: { bg: 'error.soft' } },
      false: {},
    },
    disabled: {
      true: {
        color: 'text.tertiary',
        cursor: 'not-allowed',
        opacity: 0.55,
        _hover: { bg: 'transparent' },
      },
      false: {},
    },
  },
});

const menuDivider = css({
  height: '1px',
  bg: 'border.subtle',
  marginY: '2',
});

/**
 * Mobile-only top bar.
 * Layout : `[logo + recop.app] [Avatar▾]`
 *
 * The avatar opens a popover that exposes the secondary actions previously
 * housed in the desktop sidebar's user-pill (settings, theme toggle, logout).
 */
export const MobileTopBar: FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme: currentTheme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { language, setLanguage } = useUserLanguage();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  return (
    <>
      <header className={root}>
        <span className={brand}>
          <span className={brandSquare}>
            <Logo variant="cube" size={16} color="#0A0A0A" />
          </span>
          <span className={brandText}>recop.app</span>
        </span>

        {user && (
          <Popover
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
            placement="bottom-end"
            trigger={
              <span className={avatarTrigger} role="button" tabIndex={0} aria-label={t('userMenu.openUserMenu')}>
                <Avatar src={user.picture} name={user.name ?? user.email} size="sm" />
              </span>
            }
          >
            <div className={menuList}>
              <button
                type="button"
                className={menuItem({})}
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/subscription');
                }}
              >
                <Crown size={14} strokeWidth={1.75} />
                {t('userMenu.subscription')}
              </button>
              <button
                type="button"
                className={menuItem({})}
                onClick={() => {
                  setUserMenuOpen(false);
                  setIsSettingsOpen(true);
                }}
              >
                <Settings size={14} strokeWidth={1.75} />
                {t('userMenu.settings')}
              </button>
              <button
                type="button"
                className={menuItem({})}
                onClick={() => {
                  setUserMenuOpen(false);
                  toggleTheme();
                }}
              >
                {currentTheme === 'dark'
                  ? <Sun size={14} strokeWidth={1.75} />
                  : <Moon size={14} strokeWidth={1.75} />}
                {currentTheme === 'dark' ? t('userMenu.lightMode') : t('userMenu.darkMode')}
              </button>
              <button type="button" className={menuItem({})} onClick={toggleLanguage}>
                <Languages size={14} strokeWidth={1.75} />
                {t('userMenu.language')} : {language === 'en' ? 'EN' : 'FR'}
              </button>
              <a
                href={import.meta.env.VITE_DISCORD_INVITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={menuItem({})}
                style={{ textDecoration: 'none' }}
                onClick={() => setUserMenuOpen(false)}
              >
                <DiscordIcon size={14} />
                Discord
              </a>
              <div className={menuDivider} />
              <button
                type="button"
                className={menuItem({ danger: true })}
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
              >
                <LogOut size={14} strokeWidth={1.75} />
                {t('userMenu.logout')}
              </button>
            </div>
          </Popover>
        )}
      </header>

      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </>
  );
};
